// docTree.mjs — pure tree algorithms for the doc browser (SB-042: extracted from
// DocBrowserPage.jsx per the docRoute/workDays template — no React, no DOM,
// unit-testable). Nodes are the /api/docs/tree shape: { docKey, isDoc, viewable,
// hasContent, children? } — pre-authorized and pre-sorted by the backend.

// docKeys of every folder node (has children) — the A3 expand-all target set.
export function collectFolderKeys(nodes, acc = []) {
  for (const n of nodes || []) {
    if (Array.isArray(n.children) && n.children.length > 0) {
      acc.push(n.docKey);
      collectFolderKeys(n.children, acc);
    }
  }
  return acc;
}

// Find a node by docKey (hint only — a link target may legitimately have no node:
// B17-concealed subtrees are pruned server-side).
export function findNode(nodes, docKey) {
  for (const n of nodes || []) {
    if (n.docKey === docKey) return n;
    if (Array.isArray(n.children) && n.children.length > 0) {
      const hit = findNode(n.children, docKey);
      if (hit) return hit;
    }
  }
  return null;
}

// A4: first selectable doc in reading order (tree arrives pre-sorted from the
// backend), plus the ancestor-folder docKeys to expand so it is directly clickable.
export function findFirstDoc(nodes, trail = []) {
  for (const n of nodes || []) {
    if (n.isDoc && n.viewable && n.hasContent) return { node: n, ancestors: trail };
    if (Array.isArray(n.children) && n.children.length > 0) {
      const found = findFirstDoc(n.children, [...trail, n.docKey]);
      if (found) return found;
    }
  }
  return null;
}

// ── 空树的两种理由（第一百一十一批集成）──────────────────────────────────────
// `GET /api/docs/tree` 从本批起投影 `contentState`（值域 never_published /
// stale_pending / published），而在此之前**空树只有一句话**。两件完全不同的事
// 因此长得一模一样：
//   · 这个组还没有任何发布产物（三个 peer 组现在就是这样——通道刚通，内容还没并进来）
//   · 这个组发布过，但按你的职位一篇都看不到
// 第一句该说的是「东西还没来」，第二句该说的是「东西在，但不给你看」。把它们
// 折成同一句「暂无可见文档」，正是 SB-120 被特许消灭的那个形状——而 A 线在服务端
// 把这两件事分开之后，**没有任何一处读它**，于是那次分开在产品面上等于没发生。
//
// ⚠ 写成纯函数而不是 JSX 里的三元：值域是服务端给的，它会长。表驱动 + 值域断言
// 才能让「服务端加了第四态而前端没跟上」当场红，而不是静静落回默认那一句。
export const DOC_TREE_CONTENT_STATES = ['never_published', 'stale_pending', 'published'];

// contentState → 空树时该说哪一句 i18n 键。
// 未知值（含缺席、老服务端不投影这个字段）一律回退到那句最弱的断言——
// 「我看不到文档」永远是真的，而「东西还没来」在不知道时说出来就是编造。
export function emptyTreeReasonKey(contentState) {
  return contentState === 'never_published'
    ? 'docs.tree.neverPublished'
    : 'docs.tree.empty';
}

// ── 正文面的过时横幅（第一百一十一批处置轮 R15）──────────────────────────────
// `GET /api/docs/content` 也投影 contentState 与 freshness，而这两个键此前
// **一个消费方都没有**：`grep -rn 'freshness|behindMs|peerLatest' src` 零命中。
// routes_docs.cpp 给这一段写的理由正是「投影落在文档面上是**承重的**——落在管理面
// 上等于没落」，于是"已修复"的结论下面躺着与开工时逐字同形的病灶：
// **人真正读文档的那一页上，一个字都没落。**
//
// `stale_pending` 在树那一侧完全惰性（emptyTreeReasonKey 把它映射回
// docs.tree.empty，且只在空树时才被查）——而这一态恰恰只在**非空**树上有意义：
// 有东西可读、但你读到的不是最新的那一版。
// 回 null = 这一页不该挂横幅（`published` 是正常态，不值得每次刷屏；未知值同样回
// null——编一句关于自己不认识的状态的话，比不说更坏）。
export function docStalenessKey(contentState) {
  return contentState === 'stale_pending' ? 'docs.content.stalePending' : null;
}

// freshness → 横幅下面那半句「落后多久」。
// ⚠ **`behindMs == null` 必须被说出来**（契约 B5b③，与 CLI 的 freshnessLines 同一条
// 判据的两个投影）。最容易写错的是 `behindMs > 0 ? 警告 : 已是最新` —— 那一行代码把
//「算不出来」渲染成「已是最新」，正好把本批要解决的问题反向做实。
// 返回 { key, hours } 或 null（没有 freshness 块 ⇒ 不编造）。
export function freshnessDetail(freshness) {
  if (!freshness || typeof freshness !== 'object') return null;
  const behind = freshness.behindMs;
  if (behind === null || behind === undefined) return { key: 'docs.content.behindUnknown' };
  const n = Number(behind);
  if (!Number.isFinite(n) || n <= 0) return null;
  return { key: 'docs.content.behindBy', hours: Math.max(1, Math.round(n / 3_600_000)) };
}

// ── 单语文档的另一侧语言（2026-08-18，外来单语发布模式 SOP-14 §7）────────────
// 外来组只发 zh 页：/api/docs/content?lang=en 对它是 404「not published in this
// language」。**最小诚实**处理：内容端点 404 时按这只函数再试一次另一侧——命中即
// 展示真实存在的那一页（配下面的提示条），两侧都 404 才落回原来的归类。绝不造假
// en 页——给的是 zh 原文加一句明说。
export function contentLangFallback(lang) {
  return lang === 'zh' ? 'en' : 'zh';
}

// 界面语言下缺页时该说哪一句提示的 i18n 键。
//   · 文档只有 zh（外来单语的形状）⇒ 专句「此文档仅有中文原文」；
//   · 其它缺语组合 ⇒ null，调用方沿用通用的 docs.content.langUnavailableDesc——
//     对不认识的组合编专句，比通用的那句更坏（emptyTreeReasonKey 同一条纪律）。
export function zhOnlyNoticeKey(uiLang, availableLangs) {
  if (!Array.isArray(availableLangs) || availableLangs.includes(uiLang)) return null;
  return availableLangs.length === 1 && availableLangs[0] === 'zh'
    ? 'docs.content.zhOnlyNotice'
    : null;
}
